<template>
  <DialogLayout class-name="file-confirm-dialog" @close="emit('close')">
    <template #title>{{ props.peerName }} <span style="font-weight: 400">{{ t('chat.sendTo') }}</span></template>

    <div class="file-confirm-content">
      <img src="@/assets/icons/file.svg" alt="file" class="file-confirm-icon" />
      <div class="file-confirm-info">
        <span class="file-confirm-name">{{ props.file?.name }}</span>
        <span class="file-confirm-size">{{ formatFileSize(props.file?.size) }}</span>
      </div>
    </div>

    <template #footer>
      <el-button class="confirm-button-default" @click="emit('cancel')">{{ t('chat.cancel') }}</el-button>
      <el-button type="primary" :loading="sending" @click="handleConfirm">{{ t('chat.send') }}</el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { FileConfirmDialogProps } from './file-confirm-dialog-types'
import { useChatStore } from '@/store/modules/chat'

const { t } = useI18n()
const props = defineProps<FileConfirmDialogProps>()
const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
  (e: 'close'): void
}>()

const sending = ref(false)
const chatStore = useChatStore()

async function handleConfirm() {
  sending.value = true
  try {
    await chatStore.sendMessage('', [props.file])
    emit('confirm')
  } catch {
    ElMessage.error(t('chat.sendFailed'))
  } finally {
    sending.value = false
  }
}

function formatFileSize(size?: number): string {
  if (!size) return ''
  if (size < 1024) return `${size}B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`
  return `${(size / 1024 / 1024).toFixed(1)}MB`
}
</script>

<style lang="scss">
.file-confirm-dialog {
  min-width: 500px;
}
</style>

<style scoped lang="scss">
.file-confirm-content {
  display: flex;
    align-items: center;
    gap: 8px;
    background: #FAF8FF;
    border: 1px solid #BC97FC;
    border-radius: 8px;
    padding: 6px 8px;
  
    .file-confirm-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }

  .file-confirm-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow: hidden;

    .file-confirm-name {
      font-size: 14px;
      color: #495057;
      font-weight: 400;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .file-confirm-size {
      font-size: 12px;
      color: #86909c;
    }
  }
}
</style>
